import { ReactNode } from "react";
import { Dialog } from "primereact/dialog";
import { FormActions } from "./FormActions";

/**
 * SYSTEM_DESIGN.md §6.3 — Modales.
 *
 *   variant="form"         → footer [Acción Primaria] + [Cancelar], en ese orden
 *   variant="detail"       → sin footer: se cierra con la X del encabezado
 *   variant="destructive"  → mismo orden, primario danger; el mask no cierra
 *
 * El footer lo arma el componente. Un modal de formulario no puede invertir el orden.
 *
 * §5 — el padding interno sale de los tokens (32px 40px 40px).
 */
interface BaseProps {
  visible: boolean;
  onHide: () => void;
  header: ReactNode;
  children: ReactNode;
  style?: React.CSSProperties;
  className?: string;
}

type AppDialogProps =
  | (BaseProps & {
      variant: "detail";
    })
  | (BaseProps & {
      variant: "form";
      primaryLabel: string;
      onPrimary: () => void;
      loading?: boolean;
      primaryDisabled?: boolean;
    })
  | (BaseProps & {
      variant: "destructive";
      primaryLabel: string;
      onPrimary: () => void;
      loading?: boolean;
      primaryDisabled?: boolean;
    });

export const AppDialog = (props: AppDialogProps) => {
  const { visible, onHide, header, children, style, className } = props;

  const footer =
    props.variant === "detail" ? undefined : (
      <FormActions
        type="button"
        align="end"
        primaryLabel={props.primaryLabel}
        onPrimary={props.onPrimary}
        onCancel={onHide}
        loading={props.loading}
        disabled={props.primaryDisabled}
        primarySeverity={props.variant === "destructive" ? "danger" : undefined}
        primaryIcon={props.variant === "destructive" ? "pi pi-exclamation-triangle" : undefined}
      />
    );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={header}
      footer={footer}
      className={className}
      style={{ width: "min(90vw, 640px)", ...style }}
      contentStyle={{ padding: "var(--space-32) var(--space-40) var(--space-40)" }}
      draggable={false}
      dismissableMask={props.variant !== "destructive"}
    >
      {children}
    </Dialog>
  );
};
